import { useEffect, useState } from "react";

/**
 * useDefaultCountry — best-effort detection of the visitor's ISO-2 country code.
 *
 * Resolution order:
 *  1. Explicit `prefer` prop (the page/component can hard-code its target market,
 *     e.g. "AU" on the /online-tuition-australia hub).
 *  2. Cached value in sessionStorage (set on first successful detection — avoids
 *     repeated network round-trips on multi-form pages).
 *  3. Free IP geolocation endpoint `https://ipapi.co/country/`.
 *  4. Hard fallback "IN" — our primary market.
 *
 * Returns `[country, setCountry]`. Callers may override the detected country
 * directly through the picker; setting it persists nothing — the next page
 * load re-detects unless the visitor's IP / preferred country hasn't changed.
 */
const CACHE_KEY = "at_default_country_iso";

export default function useDefaultCountry(prefer) {
    const [country, setCountry] = useState(() => {
        if (prefer) return prefer.toUpperCase();
        try {
            const cached = sessionStorage.getItem(CACHE_KEY);
            if (cached) return cached;
        } catch { /* private mode etc. */ }
        return "IN";
    });

    useEffect(() => {
        // `prefer` is treated as read-only after mount — callers always pass a
        // static page-context value (e.g. "AU" on /online-tuition-australia).
        // If it IS set we skip IP detection entirely so the explicit page
        // intent wins.
        if (prefer) return;
        try {
            if (sessionStorage.getItem(CACHE_KEY)) return; // already cached
        } catch { /* ignore */ }

        let cancelled = false;
        const controller = new AbortController();
        const t = setTimeout(() => controller.abort(), 2500);

        const base = process.env.REACT_APP_BACKEND_URL || "";
        fetch(`${base}/api/geo/country`, { signal: controller.signal })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then((data) => {
                if (cancelled) return;
                const code = (data?.country || "").trim().toUpperCase();
                if (code && code.length === 2) {
                    try { sessionStorage.setItem(CACHE_KEY, code); } catch { /* ignore */ }
                    setCountry(code);
                }
            })
            .catch(() => { /* keep current default — no console spam */ })
            .finally(() => clearTimeout(t));

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [prefer]);

    return [country, setCountry];
}
